import React from 'react'
import {Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'

export interface MaterialRequestRow {
  _id: string
  materialId?: string
  materialName?: string
  code?: string
  requestedQuantity?: number | string
  requestDate?: string
  [key: string]: any
}

interface MaterialRequestColumnConfig {
  id: string
  label: string
}

// Same value/label pairs as the legacy page (src/pages/materialRequests/MaterialRequests.tsx) —
// kept here as the fallback so both stacks show identical Italian labels when the PSP
// hasn't overridden `materialRequestsConfigurations.columnOptions`.
const DEFAULT_COLUMNS: MaterialRequestColumnConfig[] = [
  {id: 'materialName', label: 'NOME'},
  {id: 'requestedQuantity', label: 'QUANTITÀ RICHIESTA'},
  {id: 'requestDate', label: 'DATA DELLA RICHIESTA'},
]

function renderCell(id: string) {
  return (row: MaterialRequestRow): React.ReactNode => {
    if (id === 'requestDate') {
      return <Typography variant="body2">{row.requestDate ? moment(row.requestDate).format('DD/MM/YYYY') : '—'}</Typography>
    }
    return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
  }
}

// Shared by buildColumns and getActiveColumnIds so both agree on which columns are
// actually in play — PSP-configured columnOptions when present, DEFAULT_COLUMNS otherwise.
function getActiveColumns(columnConfig?: MaterialRequestColumnConfig[]): MaterialRequestColumnConfig[] {
  return columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS
}

/**
 * The ids of the columns actually shown (PSP-configured, or DEFAULT_COLUMNS as fallback).
 * Lets the page's "Filtri" drawer hide a filter whose corresponding column was turned off
 * in the PSP config, instead of offering a filter for data the table never displays.
 */
export function getActiveColumnIds(columnConfig?: MaterialRequestColumnConfig[]): Set<string> {
  return new Set(getActiveColumns(columnConfig).map(col => col.id))
}

/**
 * Columns are PSP-driven (`materialRequestsConfigurations.columnOptions`), falling back
 * to a sensible default set — mirrors the pattern in echocardiographicRequestsColumns.tsx.
 * `materialName` renders as the mobile card header, same convention used elsewhere in
 * pages-new. Unlike the echo list, there is no row-actions column here — the legacy
 * table has none either (only the top-level "Richiedi materiale" button).
 */
export function buildColumns(columnConfig?: MaterialRequestColumnConfig[]): ColumnDef<MaterialRequestRow>[] {
  const config = getActiveColumns(columnConfig)
  return config.map((col, index) => ({
    key: `${col.id}-${index}`,
    header: col.label,
    mobileCardHeader: col.id === 'materialName',
    sortKey: col.id === 'requestDate' ? 'requestDate' : undefined,
    render: renderCell(col.id),
  }))
}
